import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import Button from 'core-system/Button'
import Chip from 'core-system/Chip'
import { useSelector, useDispatch } from 'react-redux'
import { AppState } from 'redux/config/store'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'

// ─── Layout ───────────────────────────────────────────────────────────────────

const PageContainer = styled.div`
  max-width: 42rem;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const Card = styled.div`
  ${(props) => props.theme.baseCard};
  display: flex;
  flex-direction: column;
`

// ─── Card header ──────────────────────────────────────────────────────────────

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  margin-bottom: 1.25rem;
`

const IconBadge = styled.div`
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 0.625rem;
  background-color: ${(props) => props.theme.palette.primary.pink100};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

// ─── Input area ───────────────────────────────────────────────────────────────

const InputRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  margin-bottom: 0.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    flex-direction: column;
  }
`

const InputWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`

const CardInput = styled.input<{ hasError: boolean }>`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1.0625rem;
  letter-spacing: 0.075em;
  font-family: 'Courier New', monospace;
  border: 1.5px solid
    ${(props) =>
      props.hasError
        ? props.theme.palette.error.red400
        : props.theme.palette.grey.grey300};
  border-radius: 0.5rem;
  outline: none;
  background: ${(props) => props.theme.palette.white};
  color: ${(props) => props.theme.palette.text.primary};
  transition: border-color 0.15s;

  &:focus {
    border-color: ${(props) =>
      props.hasError
        ? props.theme.palette.error.red400
        : props.theme.palette.primary.pink500};
  }

  &::placeholder {
    color: ${(props) => props.theme.palette.text.placeholder};
    letter-spacing: 0.02em;
    font-family: inherit;
  }
`

const HintRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 1.25rem;
`

const ErrorText = styled.span`
  font-size: 0.8125rem;
  color: ${(props) => props.theme.palette.error.red500};
`

const HintText = styled.span`
  font-size: 0.8125rem;
  color: ${(props) => props.theme.palette.text.secondary};
`

// ─── Portal badge ─────────────────────────────────────────────────────────────

const PortalDetectedBox = styled.div<{ variant: 'nextgen' | 'legacy' }>`
  margin-top: 1rem;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  background-color: ${(props) =>
    props.variant === 'nextgen'
      ? props.theme.palette.accent.blue50
      : props.theme.palette.primary.pink50};
  border: 1px solid
    ${(props) =>
      props.variant === 'nextgen'
        ? props.theme.palette.accent.blue200
        : props.theme.palette.primary.pink200};
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`

const PortalTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const PortalLabel = styled.span<{ variant: 'nextgen' | 'legacy' }>`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${(props) =>
    props.variant === 'nextgen'
      ? props.theme.palette.accent.blue600
      : props.theme.palette.primary.pink600};
`

const PortalDescription = styled.span`
  font-size: 0.8125rem;
  color: ${(props) => props.theme.palette.text.secondary};
`

// ─── Status tracker ───────────────────────────────────────────────────────────

const StatusTracker = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding-top: 0.25rem;
`

const StatusStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
`

const StepConnector = styled.div<{ active: boolean }>`
  position: absolute;
  top: 0.875rem;
  left: 50%;
  right: -50%;
  height: 2px;
  background-color: ${(props) =>
    props.active
      ? props.theme.palette.primary.pink400
      : props.theme.palette.grey.grey200};
  z-index: 0;
`

const StepDot = styled.div<{ status: 'done' | 'active' | 'upcoming' }>`
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  z-index: 1;
  flex-shrink: 0;
  background-color: ${(props) => {
    if (props.status === 'done') return props.theme.palette.success.green500
    if (props.status === 'active') return props.theme.palette.primary.pink500
    return props.theme.palette.grey.grey200
  }};
  color: ${(props) =>
    props.status === 'upcoming'
      ? props.theme.palette.grey.grey500
      : props.theme.palette.white};
  transition: background-color 0.2s;
`

const StepLabel = styled.span<{ active: boolean }>`
  font-size: 0.6875rem;
  font-weight: ${(props) => (props.active ? 600 : 400)};
  color: ${(props) =>
    props.active
      ? props.theme.palette.text.primary
      : props.theme.palette.text.secondary};
  text-align: center;
  margin-top: 0.4rem;
  line-height: 1.3;
`

// ─── Constants ────────────────────────────────────────────────────────────────

type CardFormat = '10-digit' | '19-digit' | null

type ActivationStatus = 'idle' | 'submitted' | 'processing' | 'uploaded' | 'active'

const STATUS_STEPS: { key: ActivationStatus; label: string }[] = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'processing', label: 'Processing' },
  { key: 'uploaded', label: 'Uploaded' },
  { key: 'active', label: 'Active' },
]

const STATUS_ORDER: ActivationStatus[] = [
  'idle',
  'submitted',
  'processing',
  'uploaded',
  'active',
]

function detectFormat(raw: string): CardFormat {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 10) return '10-digit'
  if (digits.length === 19) return '19-digit'
  return null
}

function formatDisplay(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 19)
  if (digits.length <= 10) {
    // groups of 4-3-3 for 10-digit
    return digits.replace(/(\d{4})(\d{1,3})?(\d{1,3})?/, (_, a, b, c) =>
      [a, b, c].filter(Boolean).join(' ')
    )
  }
  // groups of 4 for 19-digit
  return digits.match(/.{1,4}/g)?.join(' ') ?? digits
}

// ─── Component ────────────────────────────────────────────────────────────────

const ClipperCardIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none'>
    <rect x='2' y='5' width='20' height='14' rx='2' stroke='#E0406A' strokeWidth='1.8' />
    <rect x='2' y='9' width='20' height='3' fill='#E0406A' opacity='0.25' />
    <rect x='5' y='14' width='4' height='2' rx='0.5' fill='#E0406A' />
    <rect x='11' y='14' width='6' height='2' rx='0.5' fill='#E0406A' opacity='0.5' />
  </svg>
)

const CheckIcon = () => (
  <svg width='12' height='12' viewBox='0 0 12 12' fill='none'>
    <path d='M2 6L5 9L10 3' stroke='white' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

const ClipperCardView: React.FC = () => {
  const dispatch = useDispatch()
  const { inputValue, submittedCard, status, hasError } = useSelector(
    (state: AppState) => state.employeePlatform.clipperCard
  )

  const digits = inputValue.replace(/\D/g, '')
  const format: CardFormat = detectFormat(inputValue)
  const isComplete = format !== null
  const currentStatusIndex = STATUS_ORDER.indexOf(status)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 19)
    dispatch(employeePlatformActions.setClipperInputValue(formatDisplay(raw)))
  }

  const handleSubmit = () => {
    if (!isComplete) {
      dispatch(employeePlatformActions.setClipperHasError(true))
      return
    }
    dispatch(employeePlatformActions.submitClipperCard())
  }

  const stepStatus = (step: ActivationStatus): 'done' | 'active' | 'upcoming' => {
    const stepIdx = STATUS_ORDER.indexOf(step)
    if (stepIdx < currentStatusIndex) return 'done'
    if (stepIdx === currentStatusIndex) return 'active'
    return 'upcoming'
  }

  return (
    <PageContainer>
      {/* ── Enrollment card ── */}
      <Card>
        <CardHeaderRow>
          <IconBadge>
            <ClipperCardIcon />
          </IconBadge>
          <div>
            <Text variant='h5'>Clipper Card Enrollment</Text>
            <Text variant='body2' style={{ color: 'inherit', opacity: 0.6, marginTop: '0.125rem' }}>
              Link your Clipper card to activate your BayPass benefit
            </Text>
          </div>
        </CardHeaderRow>

        <Text variant='body2' style={{ marginBottom: '1rem', opacity: 0.75 }}>
          Enter your Clipper card number exactly as it appears on the back of
          your card. We&apos;ll automatically detect the card format and route
          it to the correct portal.
        </Text>

        <InputRow>
          <InputWrapper>
            <CardInput
              hasError={hasError}
              placeholder='e.g. 0123 456 789 or 0123 4567 8901 2345 678'
              value={inputValue}
              onChange={handleChange}
              inputMode='numeric'
              disabled={status !== 'idle'}
            />
            <HintRow>
              {hasError ? (
                <ErrorText>
                  Please enter a valid 10- or 19-digit Clipper card number.
                </ErrorText>
              ) : digits.length > 0 && !isComplete ? (
                <HintText>
                  {digits.length} digit{digits.length !== 1 ? 's' : ''} entered
                  &nbsp;—&nbsp;
                  {digits.length < 10
                    ? `${10 - digits.length} more for a 10-digit card`
                    : `${19 - digits.length} more for a 19-digit card`}
                </HintText>
              ) : isComplete ? (
                <Chip variant='green' width='auto'>
                  {format} detected
                </Chip>
              ) : null}
            </HintRow>
          </InputWrapper>

          {status === 'idle' && (
            <Button
              variant='primary'
              size='medium'
              onClick={handleSubmit}
              disabled={!isComplete}
              style={{ marginTop: '0.1rem', flexShrink: 0 }}
            >
              Submit
            </Button>
          )}
        </InputRow>

        {isComplete && status === 'idle' && (
          <PortalDetectedBox variant={format === '19-digit' ? 'nextgen' : 'legacy'}>
            <div style={{ marginTop: '0.1rem' }}>
              {format === '19-digit' ? (
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                  <circle cx='12' cy='12' r='10' stroke='#3B82F6' strokeWidth='2' />
                  <path d='M8 12l3 3 5-5' stroke='#3B82F6' strokeWidth='2' strokeLinecap='round' />
                </svg>
              ) : (
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                  <circle cx='12' cy='12' r='10' stroke='#E0406A' strokeWidth='2' />
                  <path d='M8 12l3 3 5-5' stroke='#E0406A' strokeWidth='2' strokeLinecap='round' />
                </svg>
              )}
            </div>
            <PortalTextBlock>
              <PortalLabel variant={format === '19-digit' ? 'nextgen' : 'legacy'}>
                {format === '19-digit' ? 'NextGen Clipper Portal' : 'Classic Clipper Portal'}
              </PortalLabel>
              <PortalDescription>
                {format === '19-digit'
                  ? 'Your 19-digit card will be uploaded to the new MTC NextGen portal.'
                  : 'Your 10-digit card will be uploaded to the classic MTC portal. If your card was purchased at a TVM or Walgreens/CVS more than 10 days ago, it may route to NextGen instead.'}
              </PortalDescription>
            </PortalTextBlock>
          </PortalDetectedBox>
        )}
      </Card>

      {/* ── Activation status card (shown after submit) ── */}
      {status !== 'idle' && (
        <Card>
          <CardHeaderRow>
            <div>
              <Text variant='h5'>Activation Status</Text>
              <Text variant='body2' style={{ opacity: 0.6, marginTop: '0.125rem' }}>
                Card ending in{' '}
                <strong>{submittedCard?.replace(/\s/g, '').slice(-4)}</strong>
              </Text>
            </div>
          </CardHeaderRow>

          <StatusTracker>
            {STATUS_STEPS.map((step, idx) => {
              const s = stepStatus(step.key)
              const isLast = idx === STATUS_STEPS.length - 1
              return (
                <StatusStep key={step.key}>
                  {!isLast && (
                    <StepConnector active={s === 'done'} />
                  )}
                  <StepDot status={s}>
                    {s === 'done' ? <CheckIcon /> : idx + 1}
                  </StepDot>
                  <StepLabel active={s !== 'upcoming'}>{step.label}</StepLabel>
                </StatusStep>
              )
            })}
          </StatusTracker>

          <Text variant='body2' style={{ marginTop: '1.5rem', opacity: 0.7 }}>
            {status === 'submitted' &&
              'Your card number has been received. Expect activation within 10 days — we\'ll notify you when it\'s ready.'}
            {status === 'processing' &&
              'Your card is being processed and queued for upload to the Clipper portal.'}
            {status === 'uploaded' &&
              'Your card has been uploaded to Clipper. It should activate within 1–2 business days.'}
            {status === 'active' &&
              'Your Clipper card is active! Swipe on any Bay Area transit vehicle to begin using your benefit.'}
          </Text>

          {status === 'submitted' && (
            <Text variant='body2' style={{ marginTop: '0.75rem', opacity: 0.55, fontSize: '0.8125rem' }}>
              Experiencing issues after 10 days? Use the edit link below to
              re-submit with a corrected card number — no need to contact support.
            </Text>
          )}

          {status !== 'active' && (
            <Button
              variant='tertiary'
              size='small'
              onClick={() => dispatch(employeePlatformActions.resetClipperCard())}
              style={{ marginTop: '1.25rem', alignSelf: 'flex-start' }}
            >
              Edit card number
            </Button>
          )}
        </Card>
      )}

      {/* ── Info card ── */}
      <Card>
        <Text variant='body1' style={{ marginBottom: '0.875rem', fontWeight: 600 }}>
          What is a Clipper card?
        </Text>
        <Text variant='body2' style={{ opacity: 0.75, lineHeight: 1.6 }}>
          Clipper is the all-in-one transit card for the Bay Area, accepted on
          BART, Muni, Caltrain, AC Transit, and more. Your employer&#39;s BayPass
          benefit loads funds directly onto your card each month — no out-of-pocket
          cost for covered trips.
        </Text>
        <Text variant='body2' style={{ opacity: 0.75, lineHeight: 1.6, marginTop: '0.75rem' }}>
          Don&#39;t have a Clipper card yet?{' '}
          <a
            href='https://www.clippercard.com'
            target='_blank'
            rel='noreferrer'
            style={{ color: 'inherit', fontWeight: 600 }}
          >
            Get one at clippercard.com
          </a>{' '}
          or at any Walgreens, CVS, or transit station.
        </Text>
      </Card>
    </PageContainer>
  )
}

export default ClipperCardView
